if (!customElements.get('stepped-scrolling-content')) {
  class SteppedScrollingContent extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.viewport = this.querySelector('[data-stepped-scroll-viewport]');
      this.wrapper = this.viewport?.querySelector('.swiper-wrapper');
      this.slides = [...(this.wrapper?.children || [])];
      this.panels = [...this.querySelectorAll('[data-stepped-scroll-content]')];
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.inView = false;
      this.editorPaused = false;
      this.activeIndex = 0;
      this.initBound = this.initSwiper.bind(this);
      this.clickBound = this.handleClick.bind(this);
      this.keyBound = this.handleKeydown.bind(this);
      this.blockSelectBound = this.handleBlockSelect.bind(this);
      this.blockDeselectBound = () => { this.editorPaused = false; this.syncAutoplay(); };
      this.visibilityBound = this.syncAutoplay.bind(this);
      this.motionBound = () => {
        if (this.swiper) this.swiper.params.speed = this.getSpeed();
        this.syncAutoplay();
      };

      this.addEventListener('click', this.clickBound);
      this.addEventListener('keydown', this.keyBound);
      this.addEventListener('shopify:block:select', this.blockSelectBound);
      this.addEventListener('shopify:block:deselect', this.blockDeselectBound);
      document.addEventListener('visibilitychange', this.visibilityBound);
      this.reducedMotion.addEventListener('change', this.motionBound);

      if (typeof window.Swiper === 'undefined') {
        window.addEventListener('load', this.initBound, { once: true });
      } else {
        this.initSwiper();
      }

      if (this.viewport && typeof IntersectionObserver !== 'undefined') {
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          this.syncAutoplay();
        });
        this.intersectionObserver.observe(this.viewport);
      } else {
        this.inView = true;
        this.syncAutoplay();
      }

      if (this.viewport && typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(([entry]) => {
          if (!this.swiper || Math.abs(entry.contentRect.width - this.viewportWidth) < 1) return;
          cancelAnimationFrame(this.resizeFrame);
          this.resizeFrame = requestAnimationFrame(() => {
            this.activeIndex = this.getActiveIndex(this.swiper);
            this.swiper.destroy(true, true);
            this.swiper = null;
            this.initSwiper();
          });
        });
        this.resizeObserver.observe(this.viewport);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.clickBound);
      this.removeEventListener('keydown', this.keyBound);
      this.removeEventListener('shopify:block:select', this.blockSelectBound);
      this.removeEventListener('shopify:block:deselect', this.blockDeselectBound);
      document.removeEventListener('visibilitychange', this.visibilityBound);
      window.removeEventListener('load', this.initBound);
      this.reducedMotion?.removeEventListener('change', this.motionBound);
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.resizeFrame);
      this.swiper?.destroy(true, true);
      this.swiper = null;
      this.removeCopies();
      this.initialized = false;
    }

    getSpeed() {
      return this.reducedMotion.matches ? 0 : Number(this.dataset.transitionSpeed) || 500;
    }

    removeCopies() {
      this.wrapper?.querySelectorAll('[data-stepped-scroll-copy]').forEach(copy => copy.remove());
    }

    prepareLoop() {
      this.removeCopies();
      this.baseIndex = 0;
      if (this.slides.length < 2) return;
      const gap = Number.parseFloat(getComputedStyle(this).getPropertyValue('--stepped-scroll-current-gap')) || 0;
      const widths = this.slides.map(slide => slide.getBoundingClientRect().width);
      const cycleWidth = widths.reduce((total, width) => total + width + gap, 0);
      if (!cycleWidth) return;
      const copySets = Math.max(1, Math.ceil((this.viewport.clientWidth / 2 + Math.max(...widths)) / cycleWidth));
      this.baseIndex = copySets * this.slides.length;

      // 根据屏幕宽度补齐两侧副本，少量或窄图片也能循环；停稳后无动画归位到原始项。
      const makeCopies = () => {
        const fragment = document.createDocumentFragment();
        for (let set = 0; set < copySets; set += 1) {
          this.slides.forEach(slide => {
            const copy = slide.cloneNode(true);
            copy.setAttribute('data-stepped-scroll-copy', '');
            copy.setAttribute('aria-hidden', 'true');
            [copy, ...copy.querySelectorAll('[id], [data-shopify-editor-block]')].forEach(node => {
              node.removeAttribute('id');
              node.removeAttribute('data-shopify-editor-block');
            });
            copy.querySelectorAll('a').forEach(link => { link.tabIndex = -1; });
            fragment.appendChild(copy);
          });
        }
        return fragment;
      };
      this.wrapper.prepend(makeCopies());
      this.wrapper.append(makeCopies());
    }

    initSwiper() {
      if (this.swiper || !this.viewport || !this.slides.length || typeof window.Swiper === 'undefined') return;
      this.viewportWidth = this.viewport.clientWidth;
      // 测量时先解除首屏回退样式，让所有原始图片都参与宽度计算。
      this.viewport.classList.add('swiper-initialized');
      this.prepareLoop();
      this.swiper = new window.Swiper(this.viewport, {
        slidesPerView: 'auto',
        slidesPerGroup: 1,
        centeredSlides: true,
        initialSlide: this.baseIndex + this.activeIndex,
        spaceBetween: Number.parseFloat(getComputedStyle(this).getPropertyValue('--stepped-scroll-current-gap')) || 0,
        speed: this.getSpeed(),
        loop: false,
        watchOverflow: false,
        grabCursor: false,
        allowTouchMove: false,
        simulateTouch: false,
        a11y: { enabled: false },
        autoplay: this.dataset.autoplay === 'true' && this.slides.length > 1 ? {
          delay: Number(this.dataset.autoplayDelay) || 3000,
          disableOnInteraction: false,
          reverseDirection: this.dataset.direction === 'right',
        } : false,
        on: {
          init: (swiper) => this.showContent(swiper),
          transitionStart: () => { if (!this.resetting) this.hideContent(); },
          transitionEnd: () => { if (!this.resetting) this.settle(); },
        },
      });
      this.syncAutoplay();
    }

    getActiveIndex(swiper) {
      return Number(swiper.slides[swiper.activeIndex]?.dataset.blockIndex) || 0;
    }

    settle() {
      if (!this.swiper) return;
      this.activeIndex = this.getActiveIndex(this.swiper);
      const originalIndex = this.baseIndex + this.activeIndex;
      if (this.swiper.activeIndex !== originalIndex) {
        // 无动画归位不会产生 transitionend，先停止自动播放，避免它一直等待结束事件。
        this.swiper.autoplay?.stop();
        this.resetting = true;
        this.swiper.slideTo(originalIndex, 0, false);
        this.resetting = false;
        this.syncAutoplay();
      }
      this.showContent(this.swiper);
    }

    hideContent() {
      this.panels.forEach(panel => {
        panel.classList.remove('is-active');
        panel.setAttribute('aria-hidden', 'true');
        panel.inert = true;
      });
      this.wrapper.querySelectorAll('a').forEach(link => { link.tabIndex = -1; });
    }

    showContent(swiper) {
      this.activeIndex = this.getActiveIndex(swiper);
      this.panels.forEach((panel, index) => {
        const active = index === this.activeIndex;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
        panel.inert = !active;
      });
      this.wrapper.querySelectorAll('a').forEach(link => {
        link.tabIndex = link.closest('[data-stepped-scroll-slide]') === swiper.slides[swiper.activeIndex] ? 0 : -1;
      });
    }

    move(offset) {
      if (!this.swiper || this.swiper.animating || this.slides.length < 2) return;
      this.swiper.slideTo(this.swiper.activeIndex + offset);
    }

    handleClick(event) {
      if (event.target.closest('[data-stepped-scroll-prev]')) this.move(-1);
      else if (event.target.closest('[data-stepped-scroll-next]')) this.move(1);
    }

    handleKeydown(event) {
      if (!event.target.closest('[data-stepped-scroll-viewport]') || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }
    }

    handleBlockSelect(event) {
      const slide = event.target.closest('[data-stepped-scroll-slide]');
      if (!slide || !this.swiper) return;
      this.editorPaused = true;
      this.syncAutoplay();
      this.swiper.slideTo(this.baseIndex + Number(slide.dataset.blockIndex), 0);
      this.settle();
    }

    syncAutoplay() {
      if (!this.swiper?.params.autoplay?.enabled) return;
      const shouldPlay = this.inView && !document.hidden && !this.reducedMotion.matches && !this.editorPaused;
      if (shouldPlay && !this.swiper.autoplay.running) this.swiper.autoplay.start();
      else if (!shouldPlay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
    }
  }

  customElements.define('stepped-scrolling-content', SteppedScrollingContent);
}
